<template>
  <div class="flex gap-3 [&>*]:flex-1">
    <slot />
    <actions-dropdown
      v-if="availableDownloadActions.length > 0"
      :actions="availableDownloadActions"
      :is-loading="isDownloadInProgress"
      @click:download-invoice="downloadTransferFile(TransferFileTypes.INVOICE)"
      @click:download-remittance="requestRemittanceFile"
    >
      <app-tooltip
        heading="Generating remittance file"
        :disabled="!isRemittanceInFlight"
        trigger-class="block w-full"
        placement="left"
      >
        <button
          class="btn btn-secondary w-full"
          :disabled="isDownloadInProgress"
          data-cy="download-button"
        >
          <app-icon
            :name="isDownloadInProgress ? 'loading' : 'download'"
            :class="{ 'animate-spin': isDownloadInProgress }"
          />
          Download
        </button>
      </app-tooltip>
    </actions-dropdown>
  </div>
</template>

<script setup>
import { TransferFileStatus, TransferFileTypes, TransferStatus } from '@/constants/transfers.js';
import { useTransferFiles, useRequestTransferFile } from '@/services/transfers/queries.js';
import { useDownloadFileById } from '@/services/files/queries.js';
import ActionsDropdown from '@/components/ui/ActionsDropdown.vue';
import { computed, ref, watch, onUnmounted } from 'vue';
import { capitalizeWords } from '@/utils/string.js';
import { useAuthStore } from '@/stores/auth.js';
import { useToast } from '@/composables/useToast.js';

const { toast, dismissToast } = useToast();
const authStore = useAuthStore();

const props = defineProps({
  transferId: {
    type: String,
    required: true,
  },
  status: {
    type: String,
    required: true,
    validator(value) {
      return Object.values(TransferStatus).includes(value);
    },
  },
});

const transferId = computed(() => props.transferId);
const transferStatus = computed(() => props.status);

const { data: transferFiles, refetch: refetchTransferFiles } = useTransferFiles(transferId, transferStatus, {
  gcTime: 0,
});

const { mutate: downloadFileById, isPending: isDownloading } = useDownloadFileById();
const { mutate: requestTransferFile } = useRequestTransferFile();

const pendingRemittanceFileId = ref(null);
let remittanceToastId = null;

const downloadActionsConfig = computed(() => [
  {
    name: 'download-invoice',
    label: 'Commission invoice',
    icon: 'download',
    status: [TransferStatus.RELEASED, TransferStatus.SETTLED, TransferStatus.CANCELED],
    permission: 'transfer:download',
    disabled: !checkIfFileAvailable(TransferFileTypes.INVOICE),
  },
  {
    name: 'download-remittance',
    label: 'Commission remittance',
    icon: 'download',
    status: [TransferStatus.RELEASED, TransferStatus.SETTLED, TransferStatus.CANCELED],
    permission: 'transfer:download',
  },
]);

const checkIfFileAvailable = (fileType) => {
  return transferFiles.value?.data?.some((item) => item.feature_category === fileType) ?? false;
};

const availableDownloadActions = computed(() => {
  return downloadActionsConfig.value
    .filter((item) => !item.disabled)
    .filter((item) => item.status.includes(transferStatus.value))
    .filter((item) => authStore.hasPermission(item.permission));
});

const pendingRemittanceFile = computed(() =>
  transferFiles.value?.data?.find(
    (item) => item.feature_category === TransferFileTypes.REMITTANCE && item.id === pendingRemittanceFileId.value
  )
);

// Tracks the whole click-to-download lifecycle with a single flag instead of deriving it
// from query state, since the `transfer-files` list is invalidated but not awaited on
// request success — reading `pendingRemittanceFile` in that window is stale/undefined and
// would otherwise let the button briefly re-enable before the list catches up.
const isRemittanceInFlight = ref(false);

const isDownloadInProgress = computed(() => isDownloading.value || isRemittanceInFlight.value);

// Tears down whatever's tracking the current remittance request/generation/download,
// dismissing its toast. Used both on terminal outcomes (success/failure) and when the
// transfer being tracked is no longer the one on screen.
const resetRemittanceTracking = () => {
  isRemittanceInFlight.value = false;
  pendingRemittanceFileId.value = null;
  dismissToast(remittanceToastId);
  remittanceToastId = null;
};

watch(transferId, (newId, oldId) => {
  if (oldId != null && newId !== oldId) {
    resetRemittanceTracking();
  }
});

onUnmounted(() => {
  dismissToast(remittanceToastId);
});

const requestRemittanceFile = () => {
  isRemittanceInFlight.value = true;
  remittanceToastId = toast(
    {
      heading: 'Generating remittance file...',
      message: 'This can take up to several minutes. \nStay on this page until the download completes.',
      color: 'neutral',
      icon: 'download',
      iconVariant: 'basic',
    },
    { autoClose: false, position: 'bottom-right', clearOnUrlChange: false }
  );
  requestTransferFile(
    { id: transferId.value, featureCategory: TransferFileTypes.REMITTANCE },
    {
      onSuccess: (response) => {
        pendingRemittanceFileId.value = response?.data?.id ?? null;
      },
      onError: () => {
        resetRemittanceTracking();
      },
    }
  );
};

watch(
  () => pendingRemittanceFile.value?.status,
  (status) => {
    if (status === TransferFileStatus.SUCCEEDED) {
      const file = pendingRemittanceFile.value;
      pendingRemittanceFileId.value = null;
      downloadFileById(
        { id: file.id, fileName: file.filename },
        {
          onSettled: () => {
            resetRemittanceTracking();
          },
        }
      );
    } else if (status === TransferFileStatus.FAILED) {
      resetRemittanceTracking();
      toast({
        heading: 'Remittance generation failed',
        message: 'The remittance file could not be generated, please try again',
        color: 'error',
        icon: 'info-circle',
      });
    }
  }
);

const downloadTransferFile = (fileType) => {
  const file = transferFiles.value?.data.find((item) => item.feature_category === fileType);

  if (file?.status !== TransferFileStatus.SUCCEEDED) {
    toast({
      heading: 'File is not ready yet',
      message: `${capitalizeWords(fileType)} is being generated and will be available soon`,
      color: 'warning',
      icon: 'info-circle',
    });
    return;
  }

  downloadFileById({ id: file.id, fileName: file.filename });
};

watch(
  () => [transferId.value, transferStatus.value],
  () => {
    refetchTransferFiles();
  }
);
</script>
